// @flow

import PropTypes from 'prop-types';
import * as React from 'react';
import { findDOMNode } from 'react-dom';
import { isFeatureEnabled } from 'common/features';

import Pagination from 'common/components/Pagination';
import Render from 'common/components/Render';

import VenuePreview from 'search/components/VenuePreview';
import FeaturedVenuePreview from 'search/components/FeaturedVenuePreview';
import SearchActionCreators from 'search/actions/SearchActionCreators';

import $ from 'jquery';

import SearchHits from './searchResults/SearchHits';
import SearchSortSelect from './searchResults/SearchSortSelect';
import SlushBanner from './searchResults/SlushBanner';
import {
  default as PandemicTimeEventsBanner,
  NOT_ALLOWED_EVENT_TYPES as PANDEMIC_BANNER_NOT_ALLOWED_EVENT_TYPES
} from './searchResults/PandemicTimeEventsBanner';
import SearchInstructions from './searchResults/SearchInstructions';
import SearchResultFooter from './searchResults/SearchResultFooter';

import { trackClickedSearchResult } from 'search/analytics';

import type {
  VenueResult,
  ResultsMeta,
  Params as SearchParams
} from 'search/types';

type OwnProps = {|
  params: SearchParams,
  results: {
    meta: ResultsMeta,
    venues: Array<VenueResult>,
    featuredVenues?: Array<VenueResult>,
    ...
  },
  highlightCapacitySitting: boolean,
  openInNewTab: boolean,
  activeResultId?: number,
  isLoading?: boolean,
  features: { edit_relevance?: boolean, ... }
|};

export type Props = {|
  ...OwnProps,
  staticPagesEnabled: boolean
|};

export class SearchResultsUI extends React.Component<Props> {
  static propTypes = {
    results: PropTypes.shape({
      meta: PropTypes.object.isRequired,
      venues: PropTypes.array.isRequired
    }).isRequired,
    highlightCapacitySitting: PropTypes.bool,
    openInNewTab: PropTypes.bool,
    activeResultId: PropTypes.number,
    isLoading: PropTypes.bool,
    features: PropTypes.shape({
      edit_relevance: PropTypes.bool
    }).isRequired
  };

  render() {
    const {
      highlightCapacitySitting,
      openInNewTab,
      staticPagesEnabled
    } = this.props;
    const meta = this.props.results.meta;
    const resultsFound = !!meta.total;
    const noMorePages =
      !meta.total_pages || meta.current_page === meta.total_pages;

    const isPreviewActive = venue => this.props.activeResultId === venue.id;

    const { featuredVenues = [] } = this.props.results;
    const featuredVenuePreviewElements = featuredVenues.map((venue, index) => {
      return (
        <li key={venue.id} data-testid="featured-search-result">
          <FeaturedVenuePreview
            venue={venue}
            highlightCapacity={
              highlightCapacitySitting ? 'SITTING' : 'STANDING'
            }
            openInNewTab={openInNewTab}
            isActive={isPreviewActive(venue)}
            onClickVenueLink={() =>
              trackClickedSearchResult(venue, meta, index, 'FEATURED')
            }
          />
        </li>
      );
    });

    const venuePreviewElements = this.props.results.venues.map(
      (venue, index) => {
        return (
          <VenuePreview
            venue={venue}
            highlightCapacitySitting={highlightCapacitySitting}
            openInNewTab={openInNewTab}
            isActive={isPreviewActive(venue)}
            key={venue.id}
            features={this.props.features}
            onClickVenueLink={() =>
              trackClickedSearchResult(
                venue,
                meta,
                index + featuredVenues.length,
                'NORMAL'
              )
            }
          />
        );
      }
    );

    const banner = (() => {
      if (isFeatureEnabled('slush_banner_in_search')) {
        return <SlushBanner />;
      } else if (
        isFeatureEnabled('pandemic_time_events_banner') &&
        this.props.params.et &&
        !PANDEMIC_BANNER_NOT_ALLOWED_EVENT_TYPES.includes(
          `${this.props.params.et}`
        )
      ) {
        return <PandemicTimeEventsBanner />;
      } else {
        return null;
      }
    })();

    return (
      <div className="m-search-results b-overlay-container">
        <div className="m-search-results-sorting">
          <SearchHits
            offsetStart={meta.offset_start}
            offsetEnd={meta.offset_end}
            total={meta.total}
          />
          <SearchSortSelect
            value={this.props.params.sort}
            onValue={this._onSortChange}
          />
        </div>
        {banner}
        <ul className="b-search-result-list" data-testid="search-results">
          {featuredVenuePreviewElements}
          {venuePreviewElements}
        </ul>
        <Pagination
          currentPage={meta.current_page}
          totalPages={meta.total_pages}
          firstPageURL={meta.first_page_url}
          lastPageURL={null}
          nextPageURL={meta.next_page_url}
          previousPageURL={meta.previous_page_url}
          onPageChange={this._onPageChange}
        />
        <Render when={noMorePages}>
          <SearchInstructions resultsFound={resultsFound} />
        </Render>
        <SearchResultFooter staticPagesEnabled={staticPagesEnabled} />
        <div className="m-search-results-fade" />
        {this.props.isLoading && (
          <div className="b-overlay b-overlay--full b-overlay--light" />
        )}
      </div>
    );
  }

  _onSortChange = (sorting: ?string) => {
    SearchActionCreators.changeSorting(sorting);
  };

  _onPageChange = (nextPage: number) => {
    SearchActionCreators.changePage(nextPage);
    this._scrollToResults();
  };

  _scrollToResults = () => {
    const offsetY = $(findDOMNode(this)).offset().top - $('.m-navbar').height();
    window.scrollTo(0, offsetY);
  };
}

type ContainerState = {|
  staticPagesEnabled: boolean
|};

class SearchResultsContainer extends React.Component<OwnProps, ContainerState> {
  state = {
    staticPagesEnabled: isFeatureEnabled('static_pages')
  };

  render() {
    return <SearchResultsUI {...this.props} {...this.state} />;
  }
}

export default SearchResultsContainer;
